import * as moment from 'moment';
import {SMS, USSD, WHATSAPP} from '../../../../../shared/utils/config';
import {BubbleType} from './translatorUtils';

type OutboundTranslatorType = 'text' | 'media' | 'location' | 'button' | 'list' | 'contact';

export interface OutboundTranslator {
  channelId: number;
  integrationId: string;
  to: string;
  type: OutboundTranslatorType;
  content: string | ContentLocation | ContentMedia | ContentInteractive | Contact;
}

export interface ContentLocation {
  contentType: string;
  locationAddress: any;
  locationName: any;
  latitude: any;
  longitude: any;
}

export interface ContentInteractive {
  contentType: string;
  title: string;
  replyMessage: string;
  postbackData: string;
  messageDisplay: string;
  message?: string;
  relatedClientMessageId?: string;
}

export interface ContentMedia {
  contentType: string;
  downloadUrl: string;
  sha256Hash: string;
  byteSize: number;
  fileName: string;
  caption: string;
  reference?: string;
}

interface MediaFile {
  contentType: string;
  downloadUrl: string;
  sha256Hash: string;
  byteSize: number;
  fileName: string;
}

interface SelectedItem {
  postbackData: string;
  content?: string;
}

export interface Contact {
  contentType: string;
  addresses: [],
  emails: [],
  org: {
    company: string,
    department: string,
    title: string
  },
  phones: [],
  urls: [],
  birthday: string,
  name: {
    firstName: string,
    lastName: string,
    formattedName: string,
  }
}

interface OneApiResponse {
  integrationId: string;
  integrationName: string;
  event: {};
}

interface MoText {
  channel: string;
  messageId: string;
  relatedMessageId: any;
  relatedClientMessageId: any;
  from: string;
  to: string;
  timestamp: number;
  content: string;
  charset: string;
  whatsapp: any;
}

interface MoMedia {
  channel: string;
  messageId: string;
  relatedMessageId: any;
  relatedClientMessageId: any;
  from: string;
  to: string;
  timestamp: number;
  caption: string;
  whatsapp: any;
  files: MediaFile[];
}

interface MoLocation {
  whatsapp: any;
  charset: string;
  locationName: any;
  latitude: any;
  channel: string;
  messageId: string;
  locationAddress: any;
  relatedMessageId: any;
  from: string;
  to: string;
  relatedClientMessageId: null;
  timestamp: number;
  longitude: any;
}

interface MoList {
  channel: string;
  messageId: string;
  relatedMessageId: any;
  relatedClientMessageId: any;
  from: string;
  to: string;
  timestamp: number;
  postbackData: string;
  whatsapp: any;
  selectedItems: [SelectedItem];
}

interface MoButton {
  channel: string;
  messageId: string;
  relatedMessageId: any;
  relatedClientMessageId: any;
  from: string;
  to: string;
  timestamp: number;
  postbackData: string;
  whatsapp: any;
  selectedItem: SelectedItem;
}

interface MoContacts {
  channel: string;
  messageId: string;
  relatedMessageId: any;
  relatedClientMessageId: any;
  from: string;
  to: string;
  timestamp: number;
  whatsapp: any;
  contacts: any[];
}

interface TranslationResponse {
  channelPayload: any;
  mediaData: MediaData;
}

export interface MediaData {
  description?: string;
  fileSize?: number;
  caption?: string;
  type?: string;
  locationAddress?: any;
  locationName?: any;
  latitude?: any;
  longitude?: any;
  bubbleType: BubbleType;
  src: string;
  reference?: string;
  fileHash?: string;
  subtitle?: string;
  title?: string;
  replyMessage?: string;
  postbackData?: string;
  messageDisplay?: string;
  message?: string;
  messageId: string;
  contacts?: Contact;
}

export const outboundTranslator = (translate: OutboundTranslator): TranslationResponse => {
  const translationResponse: TranslationResponse = {
    channelPayload: {},
    mediaData: {
      src: '',
      messageId: 'test-message-' + Math.floor(Math.random() * 100000),
      bubbleType: 'text',
    },
  };

  switch (translate.channelId) {
    case USSD:
      console.error('This is not supported');
      break;
    case WHATSAPP:
      const oneApi = new OneApiTranslator(translate);
      switch (translate.type) {
        case 'location':
          translationResponse.channelPayload = oneApi.location;
          translationResponse.mediaData = metaData(translate.content as ContentLocation, oneApi.getMessageId);
          break;
        case 'media':
          translationResponse.channelPayload = oneApi.media;
          translationResponse.mediaData = metaData(translate.content as ContentMedia, oneApi.getMessageId);
          break;
        case 'button':
          translationResponse.channelPayload = oneApi.button;
          translationResponse.mediaData = metaData(translate.content as ContentInteractive, oneApi.getMessageId);
          break;
        case 'list':
          translationResponse.channelPayload = oneApi.list;
          translationResponse.mediaData = metaData(translate.content as ContentInteractive, oneApi.getMessageId);
          break;
        case 'contact':
          translationResponse.channelPayload = oneApi.contact;
          translationResponse.mediaData = metaData(translate.content as Contact, oneApi.getMessageId);
          break;
        default:
          translationResponse.channelPayload = oneApi.textWhatsapp;
          translationResponse.mediaData.messageId = oneApi.getMessageId;
          break;
      }
      break;
    case SMS:
      if (translate.type === 'media' || translate.type === 'location') {
        console.error('media and location is not supported for SMS');
        break;
      }
      const smsOneApi = new OneApiTranslator(translate);
      translationResponse.channelPayload = smsOneApi.textSms;
      break;
  }
  return translationResponse;
};

const metaData = (content: ContentMedia | ContentLocation | ContentInteractive | Contact, messageId: string): MediaData => {
  const type = getContentType(content);
  switch (type) {
    case 'image':
      content = content as ContentMedia;
      return {
        src: content.downloadUrl,
        reference: content.reference,
        description: content.caption,
        fileHash: content.sha256Hash,
        messageId: messageId,
        bubbleType: 'image',
      };
    case 'video':
      content = content as ContentMedia;
      return {
        src: content.downloadUrl,
        reference: content.reference,
        type: content.contentType,
        fileHash: content.sha256Hash,
        messageId: messageId,
        bubbleType: 'video',
      };
    case 'audio':
      content = content as ContentMedia;
      return {
        src: content.downloadUrl,
        reference: content.reference,
        fileHash: content.sha256Hash,
        messageId: messageId,
        bubbleType: 'voicenote',
      };
    case 'application':
      content = content as ContentMedia;
      return {
        description: content.fileName,
        fileSize: content.byteSize,
        src: content.downloadUrl,
        reference: content.reference,
        fileHash: content.sha256Hash,
        type: content.contentType,
        messageId: messageId,
        bubbleType: 'file',
      };
    case 'location':
      content = content as ContentLocation;
      return {
        locationName: content.locationName,
        locationAddress: content.locationAddress,
        latitude: content.latitude,
        longitude: content.longitude,
        src: '',
        messageId: messageId,
        bubbleType: 'location',
      };
    case 'list':
      content = content as ContentInteractive;
      return {
        title: content.title,
        replyMessage: content.replyMessage,
        postbackData: content.postbackData,
        messageDisplay: content.messageDisplay,
        src: '',
        messageId: messageId,
        bubbleType: 'interactive',
      };
    case 'button':
      content = content as ContentInteractive;
      return {
        title: content.title,
        replyMessage: content.replyMessage,
        postbackData: content.postbackData,
        messageDisplay: content.messageDisplay,
        message: content.message,
        src: '',
        messageId: messageId,
        bubbleType: 'interactive',
      };
    case 'contact':
      return {
          src: '',
          messageId: messageId,
          contacts: content as Contact,
          bubbleType: 'contact',
      };
    default:
      return;
  }
};

const getContentType = (content: ContentMedia | ContentLocation | ContentInteractive | Contact) => {
  const contentType = content.contentType;
  if (contentType.includes('/')) {
    return content ? content.contentType.split('/')[0] : null;
  }

  return contentType
}

class OneApiTranslator {
  translate: OutboundTranslator;

  oneApiResponse: OneApiResponse = {
    integrationId: '',
    integrationName: '',
    event: null,
  };

  messageId = 'test-message-' + Math.floor(Math.random() * 100000);

  constructor(translate: OutboundTranslator) {
    this.translate = translate;
    this.oneApiResponse.integrationId = translate.integrationId;
    this.oneApiResponse.integrationName = 'Test Integration';
  }

  get getMessageId() {
    return this.messageId;
  }

  get media() {
    const content: ContentMedia = this.translate.content as ContentMedia;
    const moMedia: MoMedia = {
      channel: 'whatsapp',
      messageId: this.messageId,
      relatedMessageId: null,
      relatedClientMessageId: null,
      from: this.translate.to,
      to: '2799900001',
      timestamp: moment.now(),
      files: [
        {
          contentType: content.contentType,
          downloadUrl: content.downloadUrl,
          sha256Hash: content.sha256Hash,
          byteSize: content.byteSize,
          fileName: content.fileName,
        },
      ],
      caption: content.caption,
      whatsapp: {
        profileName: 'Profile Name',
      },
    };

    this.event = { moMedia: [moMedia] };
    return this.oneApiResponse;
  }

  get list() {
    const content: ContentInteractive = this.translate.content as ContentInteractive;
    const moListPickerResponse: MoList = {
      channel: 'whatsapp',
      messageId: this.messageId,
      relatedMessageId: null,
      relatedClientMessageId: content.relatedClientMessageId,
      from: this.translate.to,
      to: '2799900001',
      timestamp: moment.now(),
      postbackData: null,
      selectedItems: [{
          postbackData: content.postbackData,
          content: content.messageDisplay
        }],
      whatsapp: {
        profileName: 'Profile Name',
      },
    };

    this.event = { moListPickerResponse: [moListPickerResponse] };
    return this.oneApiResponse;
  }

  get button() {
    const content: ContentInteractive = this.translate.content as ContentInteractive;

    const moButton: MoButton = {
      channel: 'whatsapp',
      messageId: this.messageId,
      relatedMessageId: null,
      relatedClientMessageId: content.relatedClientMessageId,
      from: this.translate.to,
      to: '2799900001',
      timestamp: moment.now(),
      postbackData: null,
      selectedItem: {
          postbackData: content.postbackData,
          content: content.messageDisplay
        },
      whatsapp: {
        profileName: 'Profile Name',
      },
    };

    this.event = { moButtonResponse: [moButton] };
    return this.oneApiResponse;
  }

  get location() {
    const content: ContentLocation = this.translate.content as ContentLocation;
    const moLocation: MoLocation = {
      channel: 'whatsapp',
      messageId: this.messageId,
      relatedMessageId: null,
      relatedClientMessageId: null,
      from: this.translate.to,
      to: '2799900001',
      timestamp: moment.now(),
      charset: 'UTF-8',
      locationAddress: content.locationAddress,
      locationName: content.locationName,
      latitude: content.latitude,
      longitude: content.longitude,
      whatsapp: {
        profileName: 'Profile Name',
      },
    };
    this.event = { moLocation: [moLocation] };
    return this.oneApiResponse;
  }

  get textWhatsapp() {
    const moText = this.getText('whatsapp');
    this.event = { moText: [moText] };
    return this.oneApiResponse;
  }

  get textSms() {
    const moText = this.getText('sms');
    this.event = { moText: [moText] };
    return this.oneApiResponse;
  }

  private getText(channel: string) {
    const moText: MoText = {
      channel: channel,
      messageId: this.messageId,
      relatedMessageId: null,
      relatedClientMessageId: null,
      from: this.translate.to,
      to: '2799900001',
      timestamp: moment.now(),
      content: this.translate.content as string,
      charset: 'UTF-8',
      whatsapp: {
        profileName: 'Profile Name',
      },
    };
    return moText;
  }

  get contact() {
    const contact = this.translate.content as Contact;

    const moContacts: MoContacts = {
        channel: 'whatsapp',
        messageId: this.messageId,
        relatedMessageId: null,
        from: this.translate.to,
        to: '2799900001',
        timestamp: moment.now(),
        contacts: [{
            addresses:  contact.addresses,
            birthday: contact.birthday,
            emails: contact.emails,
            name: contact.name,
            org: contact.org,
            phones: contact.phones,
            urls:  contact.urls
        }],
        whatsapp: {
          profileName: 'Profile Name',
        },
        relatedClientMessageId: null
    };

    this.event = { moContacts: [moContacts] };
    return this.oneApiResponse;
  }


  private set event(moEvent: {}) {
    this.oneApiResponse.event = moEvent;
  }
}
